/**
 * t554u: Store sales, shared by the desktop page (Home › Store sales) and the Store sales card on the phone Dashboard.
 * One endpoint (GET /sales/report): the API checks sales.view per building and adds up only those buildings.
 * The building and the period are remembered on this device for the workspace and person.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import {
  ApiError, SALES_PHONE_PERIODS, SALES_SCOPE_ALL, formatGBP, formatGbp, hourAxisLabel, hourLabel, salesChangeShort, salesChangeText, salesDashboardPickKey, salesReportPeriodKey, salesReportPickKey,
  type SalesReport, type SalesTone, type SalesReportPeriod, type SalesReportProduct,
} from '@tradeoutlet/shared';
import { api } from '@/lib/api';
import { useMe, useWorkspaceId } from '@/lib/hooks';
import { relative } from '@/lib/format';
import { LONDON, parseRange, presetValue, type DateRangeValue, type RangePreset } from '@/lib/dateRange';

export const DESKTOP_PRESETS: RangePreset[] = ['today', 'yesterday', 'this-week', 'last-week', 'this-month', 'last-month', 'custom'];
export const muted = 'text-slate-500 dark:text-slate-400';

const read = (k: string | null) => { if (!k) return null; try { return localStorage.getItem(k); } catch { return null; } };
const write = (k: string | null, v: string) => { if (!k) return; try { localStorage.setItem(k, v); } catch { /* private mode */ } };
const rangeText = (v: DateRangeValue) => (v.preset === 'custom' ? `${v.from}..${v.to}` : v.preset);

/**
 * Building + period (URL first on desktop, then this device's memory), and the report for them. t584u: the phone
 * Dashboard card has its own memory and starts on All buildings; only its building menu is remembered there, and a
 * tapped building row (narrow) only narrows until you leave. On the desktop page a row is remembered as before.
 */
export function useStoreSales({ phone = false }: { phone?: boolean } = {}) {
  const ws = useWorkspaceId();
  const uid = useMe().data?.profile.user_id ?? null;
  const [sp, setSp] = useSearchParams();
  const bKey = ws && uid ? (phone ? salesDashboardPickKey(ws, uid) : salesReportPickKey(ws, uid)) : null;
  const [narrowed, setNarrowed] = useState<string | null>(null);
  const pKey = ws && uid ? salesReportPeriodKey(ws, uid, phone) : null;
  const [tick, setTick] = useState(0);
  const bump = () => setTick(t => t + 1);

  const building = (phone && narrowed) || (!phone && sp.get('building')) || read(bKey) || SALES_SCOPE_ALL;
  const range: DateRangeValue = useMemo(() => {
    const raw = (!phone && sp.get('range')) || read(pKey);
    if (phone) return presetValue(raw && SALES_PHONE_PERIODS.includes(raw as SalesReportPeriod) ? raw as Exclude<RangePreset, 'custom'> : 'today');
    return parseRange(raw, DESKTOP_PRESETS, 'today');
  }, [phone, sp, pKey, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = useCallback((next: string) => {
    write(bKey, next);
    setNarrowed(null);
    if (!phone) setSp(p => { const n = new URLSearchParams(p); n.set('building', next); return n; }, { replace: true });
    bump();
  }, [bKey, phone, setSp]);
  const setRange = useCallback((v: DateRangeValue) => {
    write(pKey, rangeText(v));
    if (!phone) setSp(p => { const n = new URLSearchParams(p); n.set('range', rangeText(v)); return n; }, { replace: true });
    bump();
  }, [pKey, phone, setSp]);

  const period = range.preset as SalesReportPeriod;
  const q = useQuery({
    queryKey: ['sales', ws, 'report', building, period, period === 'custom' ? range.from : '', period === 'custom' ? range.to : ''],
    queryFn: () => api.sales.report(ws!, { building, period, ...(period === 'custom' ? { from: range.from, to: range.to } : {}) }),
    enabled: Boolean(ws && uid),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
    retry: false,
  });
  // A remembered building I can no longer see: back to All buildings (the API refused it).
  const refused = q.error instanceof ApiError && q.error.status === 403;
  useEffect(() => { if (refused && building !== SALES_SCOPE_ALL) pick(SALES_SCOPE_ALL); }, [refused, building, pick]);
  const narrow = useCallback((id: string) => { if (phone) setNarrowed(id); else pick(id); }, [phone, pick]);
  return { ws, building: q.data?.building ?? building, range, setRange, pick, narrow, q, report: q.data ?? null, noAccess: refused && building === SALES_SCOPE_ALL };
}

/** "▲ 12% on last week", for one figure. */
export const change = (r: SalesReport, cur: number | null, prev: number | null) => salesChangeText(cur, prev, r.range.compare_label);
/** Refunds line: "2 refunds · ▼ 40%" (the change against the previous period; fewer refunds is better). */
export const refundsSub = (c: SalesReport['current'], p: SalesReport['previous']) => {
  const n = c.refunds ? `${c.refunds} refund${c.refunds === 1 ? '' : 's'}` : 'None';
  const ch = salesChangeShort(c.refunds_pence, p.refunds_pence);
  return ch === '-' || ch === 'Same' ? n : `${n} · ${ch}`;
};
export const isEmpty = (r: SalesReport) => r.current.transactions === 0 && r.current.sales_pence === 0 && r.current.refunds === 0;
export const gbpWhole = (p: number) => formatGBP(p, { whole: true });
export const items = (n: number) => (Math.round(n * 10) / 10).toLocaleString('en-GB');
export const dayLabel = (iso: string, fmt = 'ccc d LLL') => DateTime.fromISO(iso, { zone: LONDON }).setLocale('en-GB').toFormat(fmt);
export const updated = (r: SalesReport) => (r.as_of ? `Updated ${relative(r.as_of)}` : 'Not synced yet');
export const buildingName = (r: SalesReport) => (r.building === SALES_SCOPE_ALL ? 'All buildings' : r.buildings.find(b => b.id === r.building)?.name ?? '');

/**
 * t568u: green up / red down against the previous period (packages/shared salesTone; inverted for refunds), calm
 * shades with enough contrast; neutral keeps the usual colours. The change text carries ▲/▼ too.
 */
export const TONE_TEXT: Record<SalesTone, string> = {
  good: 'text-[#15803d] dark:text-[#4ade80]',
  bad: 'text-[#b91c1c] dark:text-[#f87171]',
  neutral: '',
};

/** One key figure: label, value, change line (green when better than the previous period, red when worse). */
export function Figure({ label, value, sub, testId, size = 'md', tone = 'neutral' }: { label: string; value: string; sub?: string; testId?: string; size?: 'md' | 'sm'; tone?: SalesTone }) {
  return (
    <div className="min-w-0" data-testid={testId} data-tone={tone}>
      <div className={clsx('text-xs', muted)}>{label}</div>
      <div className={clsx('font-semibold tabular-nums', tone === 'neutral' ? 'text-slate-900 dark:text-white' : TONE_TEXT[tone], size === 'md' ? 'text-2xl' : 'text-lg')}>{value}</div>
      {sub ? <div className={clsx('text-xs leading-snug', tone === 'neutral' ? muted : TONE_TEXT[tone])}>{sub}</div> : null}
    </div>
  );
}

/** Width of an element, kept up to date (0 until measured, e.g. in tests). A callback ref, so it follows remounts. */
export function useWidth<T extends HTMLElement>() {
  const [el, setEl] = useState<T | null>(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    if (!el) return;
    setW(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) => setW(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return [setEl, w] as const;
}

/** t568u: a label under every bar, its start hour centred beneath it (matches the tooltip): "13:00", or "13" when narrow. */
export function HourTicks({ hours, width, gap, className }: { hours: number[]; width: number; gap: number; className?: string }) {
  const slot = hours.length ? (width - gap * (hours.length - 1)) / hours.length : 0;
  return (
    <div className={clsx('mt-1 flex leading-none tabular-nums', muted, className ?? 'text-[11px]')} style={{ gap }} aria-hidden data-testid="sales-hour-labels">
      {hours.map((h, i) => <span key={h} className="min-w-0 flex-1 overflow-visible whitespace-nowrap text-center" data-testid="sales-hour-label">{hourAxisLabel(h, slot, i)}</span>)}
    </div>
  );
}

/** Sales by hour of day: grey bars, the busiest hour in the accent (with a dark edge so it reads on white). */
export function HourBars({ r, height = 120, compact }: { r: SalesReport; height?: number; compact?: boolean }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  if (!r.hourly.length) return <p className={clsx('text-sm', muted)}>No sales by hour yet.</p>;
  const lo = Math.min(...r.hourly.map(h => h.hour), 8);
  const hi = Math.max(...r.hourly.map(h => h.hour), 17);
  const by = new Map(r.hourly.map(h => [h.hour, h]));
  const max = Math.max(...r.hourly.map(h => h.sales_pence), 1);
  const peak = r.hourly.reduce((a, b) => (b.sales_pence > a.sales_pence ? b : a)).hour;
  const hours = Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);
  return (
    <div data-testid="sales-hourly" ref={ref}>
      <div className="flex items-end gap-[3px]" style={{ height }}>
        {hours.map(h => {
          const v = by.get(h)?.sales_pence ?? 0;
          return (
            <div key={h} className="flex h-full flex-1 items-end" data-hour={h} title={`${hourLabel(h)} · ${formatGbp(v)} · ${by.get(h)?.transactions ?? 0} transactions`}>
              <div className={clsx('w-full rounded-t-sm', h === peak && v > 0 ? 'bg-brand-yellow ring-1 ring-slate-900 dark:ring-0' : 'bg-slate-300 dark:bg-slate-600')} style={{ height: `${Math.max(v > 0 ? 2 : 0, (v / max) * 100)}%` }} />
            </div>
          );
        })}
      </div>
      <HourTicks hours={hours} width={width} gap={3} />
      {!compact && <p className={clsx('mt-1.5 text-xs', muted)}>Busiest {hourLabel(peak)}–{hourLabel(peak + 1)}</p>}
    </div>
  );
}

/** Each day: this period dark, the previous period's same day as a lighter bar behind it. */
export function DayBars({ r, height = 160, compact }: { r: SalesReport; height?: number; compact?: boolean }) {
  const max = Math.max(...r.daily.map(d => Math.max(d.sales_pence, d.previous_sales_pence ?? 0)), 1);
  const every = r.daily.length > 14 ? 7 : r.daily.length > 7 ? 2 : 1;
  return (
    <div data-testid="sales-daily">
      <div className="flex items-end gap-1" style={{ height }}>
        {r.daily.map(d => (
          <div key={d.date} className="relative flex h-full flex-1 items-end justify-center"
            title={`${dayLabel(d.date)} · ${formatGbp(d.sales_pence)}${d.previous_date ? ` · ${dayLabel(d.previous_date)} ${formatGbp(d.previous_sales_pence ?? 0)}` : ''}`}>
            {d.previous_sales_pence != null && <div className="absolute bottom-0 w-full rounded-t-sm bg-slate-200 dark:bg-slate-700" style={{ height: `${(d.previous_sales_pence / max) * 100}%` }} />}
            <div className="relative w-3/5 rounded-t-sm bg-slate-900 dark:bg-slate-100" style={{ height: `${Math.max(d.sales_pence > 0 ? 1 : 0, (d.sales_pence / max) * 100)}%` }} />
          </div>
        ))}
      </div>
      {!compact && (
        <div className={clsx('mt-1 flex gap-1 text-[11px] tabular-nums', muted)}>
          {r.daily.map((d, i) => <span key={d.date} className="flex-1 truncate text-center">{i % every === 0 ? dayLabel(d.date, r.daily.length > 7 ? 'd LLL' : 'ccc d') : ''}</span>)}
        </div>
      )}
      {!compact && <p className={clsx('mt-1 flex items-center gap-3 text-xs', muted)}>
        <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 bg-slate-900 dark:bg-slate-100" />{dayLabel(r.range.from, 'd LLL')} – {dayLabel(r.daily[r.daily.length - 1]?.date ?? r.range.to, 'd LLL')}</span>
        <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 bg-slate-200 dark:bg-slate-700" />{dayLabel(r.range.compare_from, 'd LLL')} – {dayLabel(r.range.compare_to, 'd LLL')}</span>
      </p>}
    </div>
  );
}

/** Top sellers list (rank, name, quantity, sales). */
export function TopList({ rows, by, limit = 10 }: { rows: SalesReportProduct[]; by: 'sales' | 'qty'; limit?: number }) {
  if (!rows.length) return <p className={clsx('text-sm', muted)}>No products sold.</p>;
  return (
    <ol className="divide-y divide-slate-100 dark:divide-slate-800" data-testid="sales-top">
      {rows.slice(0, limit).map((p, i) => (
        <li key={p.product_ref} className="flex items-baseline gap-3 py-1.5 text-sm">
          <span className={clsx('w-4 shrink-0 text-right tabular-nums text-xs', muted)}>{i + 1}</span>
          <span className="min-w-0 flex-1 truncate">{p.product_name}</span>
          <span className={clsx('shrink-0 tabular-nums', by === 'qty' ? 'font-medium' : muted)}>{items(p.qty)}</span>
          <span className={clsx('w-20 shrink-0 text-right tabular-nums', by === 'sales' ? 'font-medium' : muted)}>{formatGbp(p.sales_pence)}</span>
        </li>
      ))}
    </ol>
  );
}

/** Plain-text choice (no pills): the picked one is bold and underlined. */
export function TextChoice<T extends string>({ value, options, onChange, label, testId }: { value: T; options: { id: T; label: string }[]; onChange: (v: T) => void; label: string; testId?: string }) {
  return (
    <div role="group" aria-label={label} className="flex items-center gap-3 text-xs" data-testid={testId}>
      {options.map(o => (
        <button key={o.id} type="button" aria-pressed={o.id === value} onClick={() => onChange(o.id)}
          className={clsx('py-1', o.id === value ? 'font-semibold text-slate-900 underline decoration-2 underline-offset-4 dark:text-white' : clsx(muted, 'hover:text-slate-900 dark:hover:text-white'))}>
          {o.label}
        </button>
      ))}
    </div>
  );
}
